import { Phone, Mail, Video, Search, type LucideIcon } from "lucide-react";

/**
 * Engagement channels — mirrors enforcer/schema.py::Channel (the platform was
 * architected for six channels from day one). Only `voice` is wired end-to-end;
 * the rest are scaffolded placeholders so the roadmap is visible in the UI.
 *
 * Design note (Miguel, 2026-05-29): the platform orchestrates SE + captures
 * config — it does NOT host phishing infrastructure. Email engagements will
 * send the mail and reference an operator-run evilginx/phishlet instance
 * hosted OUTSIDE the platform. Keep future backends thin: orchestrate + capture
 * config, don't rebuild tooling operators already run.
 */
export type EngagementChannelKey = "voice" | "email" | "video" | "osint";

export type ChannelStatus = "live" | "planned";

export interface EngagementChannel {
  key: EngagementChannelKey;
  label: string;
  icon: LucideIcon;
  status: ChannelStatus;
  /** SE technique one-liner. */
  blurb: string;
  /** What the (future) engagement will capture/do — shown on the placeholder. */
  scaffoldNote: string;
}

export const ENGAGEMENT_CHANNELS: EngagementChannel[] = [
  {
    key: "voice",
    label: "Voice",
    icon: Phone,
    status: "live",
    blurb: "Vishing — live AI voice calls over PSTN.",
    scaffoldNote: "Fully wired: persona-driven outbound calls with the Enforcer in the loop.",
  },
  {
    key: "email",
    label: "Email",
    icon: Mail,
    status: "live",
    blurb: "Phishing — send the lure email.",
    scaffoldNote:
      "Sends the lure email through your sending profile (BYO SMTP) and links to your " +
      "operator-run relay → external evilginx. The platform composes, sends, and ingests the " +
      "click→submit funnel; you run the phishing infrastructure.",
  },
  {
    key: "video",
    label: "Video",
    icon: Video,
    status: "live",
    blurb: "Deepfake avatar calls.",
    scaffoldNote:
      "Pre-rendered consented-likeness avatar drill: configure + approve a script, " +
      "capture consent, render the clip, then drop a bot into the target's meeting to " +
      "play it. Realtime two-way avatar is on the roadmap.",
  },
  {
    key: "osint",
    label: "OSINT",
    icon: Search,
    status: "planned",
    blurb: "Recon — target enrichment.",
    scaffoldNote: "LinkedIn + breach-DB enrichment to seed pretexts and target profiles. Not yet wired.",
  },
];

export const LIVE_CHANNELS = new Set<EngagementChannelKey>(
  ENGAGEMENT_CHANNELS.filter((c) => c.status === "live").map((c) => c.key),
);
